import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import "./sstoa.css";
import { api, checkToken, clearToken, getToken } from "./api";
import Login from "./Login";
import CompanyList from "./pages/CompanyList";
import CompanyDetail from "./pages/CompanyDetail";
import CompanyForm from "./pages/CompanyForm";
import ProjectList from "./pages/ProjectList";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectForm from "./pages/ProjectForm";
import SstoaHome from "./pages/SstoaHome";
import EmailCompose from "./pages/EmailCompose";
import SchedulePage from "./pages/SchedulePage";
import CalendarPage from "./pages/CalendarPage";
import IssuesPage from "./pages/IssuesPage";
import PartsPage from "./pages/PartsPage";
import ResourcesAdmin from "./pages/ResourcesAdmin";
import ForumAdmin from "./pages/ForumAdmin";
import InquiriesPage from "./pages/InquiriesPage";
import LeadsPage from "./pages/LeadsPage";
import LandingAdmin from "./pages/LandingAdmin";
import CardsAdmin from "./pages/CardsAdmin";
import ProductsAdmin from "./pages/ProductsAdmin";
import WorksAdmin from "./pages/WorksAdmin";
import ShopOrdersAdmin from "./pages/ShopOrdersAdmin";
import LaonaDepositsAdmin from "./pages/LaonaDepositsAdmin";
import MembersAdmin from "./pages/MembersAdmin";
import DocumentCompose from "./pages/DocumentCompose";
import DocumentsList from "./pages/DocumentsList";
import DocumentTypeList from "./pages/DocumentTypeList";

/* 인증 게이트: 토큰 유효성 확인 후 통과 */
function Gate({ children }) {
  const [state, setState] = useState("checking"); // checking | ok | no
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = await checkToken();
      if (!alive) return;
      setState(ok ? "ok" : "no");
      if (!ok) navigate("/sstoa/login", { replace: true });
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  if (state === "checking") {
    return <div className="sstoa-root"><div className="sstoa-empty">확인 중…</div></div>;
  }
  if (state === "no") return null;
  return children;
}

/* 신규(미처리) 개발 문의 건수.
   화면을 옮길 때와 1분마다 확인한다. 실패하면 배지를 숨길 뿐 화면은 막지 않는다. */
function useNewInquiryCount() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;

    const fetchCount = async () => {
      try {
        const data = await api.get("/inquiries/counts");
        if (alive) setCount(data.NEW || 0);
      } catch {
        if (alive) setCount(0);
      }
    };

    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [pathname]);

  return count;
}

/* 아직 검토하지 않은(신규) 랜딩 문의 건수 */
function useNewLeadCount() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const data = await api.get("/landing/leads/counts");
        if (alive) setCount(data.NEW || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, [pathname]);

  return count;
}

/* 입금 대기 주문 건수 — 판매 주문 메뉴 배지 */
function usePendingOrderCount() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const data = await api.get("/shop-orders/counts");
        if (alive) setCount(data.PENDING || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, [pathname]);

  return count;
}

/* 입금 확인을 기다리는 라오나봇 선금 건수 — 라오나AI 선금결제 메뉴 배지 */
function usePendingLaonaDepositCount() {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const data = await api.get("/laona-deposits/counts");
        if (alive) setCount(data.PENDING || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    /* 선금 화면에서 입금을 확인하면 바로 다시 센다 */
    window.addEventListener("sstoa:laona-deposits", fetchCount);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("sstoa:laona-deposits", fetchCount); };
  }, [pathname]);

  return count;
}

/* 운영자 답을 기다리는 포럼 댓글 건수 — 포럼 관리 메뉴 배지 */
function useForumPendingCount() {
  const [count, setCount] = useState(0);
  const { pathname, search } = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const data = await api.get("/forum/counts");
        if (alive) setCount(data.PENDING || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, [pathname, search]);

  return count;
}

/* 사이드 메뉴 아이콘 — 24 격자 선 아이콘 (stroke=currentColor) */
const NAV_ICON = {
  calendar: <><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9.5h18M8 2.5v4M16 2.5v4M7.5 13.5h2M11 13.5h2M14.5 13.5h2M7.5 17h2M11 17h2" /></>,
  building: <path d="M3 21h18M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M9 8h2M9 12h2M9 16h2M15 21V10h3a1 1 0 0 1 1 1v10" />,
  folder: <><path d="M3.5 6.5a1.5 1.5 0 0 1 1.5-1.5h4l2 2h8a1.5 1.5 0 0 1 1.5 1.5v9.5a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" /><path d="M8 13h8" /></>,
  chip: <><rect x="6.5" y="6.5" width="11" height="11" rx="1.5" /><path d="M9.5 2.5v4M14.5 2.5v4M9.5 17.5v4M14.5 17.5v4M2.5 9.5h4M2.5 14.5h4M17.5 9.5h4M17.5 14.5h4" /><rect x="10" y="10" width="4" height="4" rx=".5" /></>,
  quote: <><path d="M6 3h8.5L19 7.5V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v5h5M8.5 12.5h7M8.5 16h4.5" /></>,
  order: <><path d="M3 4h2.5l2.2 10.5a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L20 7H6.2" /><circle cx="9.5" cy="19.5" r="1.3" /><circle cx="17" cy="19.5" r="1.3" /></>,
  invoice: <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2Z" /><path d="M9 8h6M9 11.5h6M9 15h3.5" /></>,
  contract: <><path d="M6 3h8.5L19 7.5V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v5h5M8.5 15.5l2.2 2.2 4.8-4.8" /></>,
  inbox: <><path d="M3 13.5 5.6 5.7A1 1 0 0 1 6.5 5h11a1 1 0 0 1 .9.7L21 13.5V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" /><path d="M3 13.5h5l1.5 2.5h5l1.5-2.5h5" /></>,
  target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r=".8" /></>,
  layout: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 9v11" /></>,
  card: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6 16c.6-1.4 1.7-2 3-2s2.4.6 3 2M15 10h3M15 13h3" /></>,
  send: <path d="M21 3 10 14M21 3l-7 18-4-7-7-4Z" />,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.8" /><path d="m21 15.5-5-5-9 9.5" /></>,
  forum: <><path d="M4 5h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" /><path d="M16 9h4a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-1v3l-4-3h-4a1 1 0 0 1-1-1v-1" /></>,
  clip: <path d="m20 11.5-7.8 7.8a5 5 0 0 1-7.1-7.1l8.1-8.1a3.3 3.3 0 0 1 4.7 4.7l-8.1 8.1a1.7 1.7 0 0 1-2.4-2.4l7.4-7.4" />,
  box: <><path d="M12 3 20.5 7.5v9L12 21l-8.5-4.5v-9Z" /><path d="M3.5 7.5 12 12l8.5-4.5M12 12v9M7.8 5.3l8.4 4.5" /></>,
  bag: <><path d="M5 8h14l-1 12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1Z" /><path d="M9 10V6.5a3 3 0 0 1 6 0V10" /></>,
  coin: <><circle cx="12" cy="12" r="8.5" /><path d="M14.8 9.2c-.5-.9-1.6-1.4-2.8-1.4-1.6 0-2.8.9-2.8 2.1 0 2.9 5.8 1.4 5.8 4.2 0 1.2-1.3 2.1-3 2.1-1.3 0-2.4-.5-2.9-1.5M12 6v1.8M12 16.2V18" /></>,
  users: <><circle cx="9" cy="8.5" r="3.5" /><path d="M2.5 20c.8-3.4 3.4-5.5 6.5-5.5s5.7 2.1 6.5 5.5" /><path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6M17.5 14.8c2 .8 3.4 2.6 4 5.2" /></>,
  home: <><path d="M4 10.5 12 4l8 6.5" /><path d="M6 9.5V20h12V9.5" /></>,
  logout: <><path d="M15 4h3.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H15" /><path d="M10 16.5 14.5 12 10 7.5M14.5 12H4" /></>,
  chev: <path d="m9 6 6 6-6 6" />,
};

function NavSvg({ name, size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {NAV_ICON[name]}
    </svg>
  );
}

/* 사이드 메뉴 — 관련된 메뉴끼리 묶는다. badge 는 Shell 의 미처리 건수 키 */
const NAV_GROUPS = [
  {
    title: "업무",
    items: [
      { to: "/sstoa/calendar", label: "일정관리", icon: "calendar" },
      { to: "/sstoa/companies", label: "업체 관리", icon: "building" },
      { to: "/sstoa/projects", label: "프로젝트 관리", icon: "folder" },
      { to: "/sstoa/parts", label: "부품 재고", icon: "chip" },
    ],
  },
  {
    title: "문서",
    items: [
      { to: "/sstoa/quotes", label: "견적서 관리", icon: "quote" },
      { to: "/sstoa/orders", label: "발주서 관리", icon: "order" },
      { to: "/sstoa/invoices", label: "인보이스 관리", icon: "invoice" },
      { to: "/sstoa/contracts", label: "계약서 관리", icon: "contract" },
    ],
  },
  {
    title: "문의",
    items: [
      { to: "/sstoa/inquiries", label: "개발 문의", icon: "inbox", badge: "inquiries", badgeTitle: "새 개발 문의" },
      { to: "/sstoa/leads", label: "랜딩 문의", icon: "target", badge: "leads", badgeTitle: "새 랜딩 문의" },
    ],
  },
  {
    title: "홍보",
    items: [
      { to: "/sstoa/landing", label: "랜딩페이지 관리", icon: "layout" },
      { to: "/sstoa/cards", label: "명함 관리", icon: "card" },
      { to: "/sstoa/email", label: "이메일 보내기", icon: "send" },
    ],
  },
  {
    title: "홈페이지",
    items: [
      { to: "/sstoa/site-works", label: "개발 사례 관리", icon: "image" },
      { to: "/sstoa/forum", label: "포럼 관리", icon: "forum", badge: "forum", badgeTitle: "답변 대기 댓글" },
      { to: "/sstoa/resources", label: "포럼 첨부파일", icon: "clip" },
      { to: "/sstoa/site-products", label: "제품 관리", icon: "box" },
      { to: "/sstoa/shop-orders", label: "판매 주문", icon: "bag", badge: "orders", badgeTitle: "입금 대기" },
      { to: "/sstoa/laona-deposits", label: "라오나AI 선금결제", icon: "coin", badge: "laona", badgeTitle: "입금 대기" },
      { to: "/sstoa/site-members", label: "회원 관리", icon: "users" },
    ],
  },
];

/* 접어 둔 메뉴 묶음 — 이 브라우저에만 기억한다. 지금 보고 있는 화면의 묶음은 늘 펼친다 */
const NAV_CLOSED_KEY = "sstoa.nav.closed.v1";
function useClosedGroups() {
  const [closed, setClosed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(NAV_CLOSED_KEY)) || []); } catch { return new Set(); }
  });
  const toggle = (title) => setClosed((prev) => {
    const next = new Set(prev);
    if (next.has(title)) next.delete(title); else next.add(title);
    try { localStorage.setItem(NAV_CLOSED_KEY, JSON.stringify([...next])); } catch { /* 저장 안 됨 */ }
    return next;
  });
  return [closed, toggle];
}

function Shell({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [closed, toggleGroup] = useClosedGroups();
  const newInquiries = useNewInquiryCount();
  const newLeads = useNewLeadCount();
  const pendingOrders = usePendingOrderCount();
  const pendingForum = useForumPendingCount();
  const pendingLaona = usePendingLaonaDepositCount();
  const badges = {
    leads: newLeads, inquiries: newInquiries, orders: pendingOrders, forum: pendingForum,
    laona: pendingLaona,
  };
  const logout = () => {
    clearToken();
    navigate("/sstoa/login", { replace: true });
  };
  const onPath = (to) => pathname === to || pathname.startsWith(`${to}/`);
  return (
    <div className="sstoa-root">
      <div className="sstoa-shell">
        <aside className="sstoa-side">
          {/* 맨 위는 늘 아이닷메타 홈페이지로 나가는 버튼 — 지우거나 옮기지 않는다 (운영자 지시).
              공개 사이트와 같은 SPA 안에 있으므로 Link 로 바로 넘어간다(새로고침 없음) */}
          <Link to="/" className="sstoa-home" title="아이닷메타 홈페이지로 가기">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                 strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 10.5 12 4l8 6.5" />
              <path d="M6 9.5V20h12V9.5" />
            </svg>
            <span>홈페이지</span>
          </Link>

          <Link to="/sstoa" className="sstoa-brand" title="SSTOA 홈">
            <span className="sstoa-brand__mark" aria-hidden="true">S</span>
            <span className="sstoa-brand__text">
              SSTOA
              <small>I.META 회사 관리</small>
            </span>
          </Link>

          <nav className="sstoa-nav" aria-label="SSTOA 메뉴">
            {NAV_GROUPS.map((g) => {
              const active = g.items.some((it) => onPath(it.to));
              const open = active || !closed.has(g.title);
              const sum = g.items.reduce((n, it) => n + (it.badge ? badges[it.badge] || 0 : 0), 0);
              return (
                <div key={g.title} className={`sstoa-nav__group${open ? "" : " is-closed"}`}>
                  <button type="button" className="sstoa-nav__title" aria-expanded={open}
                    onClick={() => !active && toggleGroup(g.title)} disabled={active}
                    title={active ? "지금 보고 있는 메뉴가 있는 묶음입니다" : open ? "접기" : "펼치기"}>
                    <span className="sstoa-nav__chev"><NavSvg name="chev" size={14} /></span>
                    <span className="sstoa-nav__gname">{g.title}</span>
                    {!open && sum > 0 && <span className="sstoa-nav__dot" title={`처리할 일 ${sum}건`}>{sum > 99 ? "99+" : sum}</span>}
                  </button>
                  {g.items.map((it) => {
                    const n = it.badge ? badges[it.badge] : 0;
                    return (
                      <NavLink key={it.to} to={it.to} title={it.label}
                        className={({ isActive }) => `sstoa-nav__item${isActive ? " is-active" : ""}`}>
                        <span className="sstoa-nav__icon"><NavSvg name={it.icon} /></span>
                        <span className="sstoa-nav__label">{it.label}</span>
                        {n > 0 && (
                          <span className="sstoa-nav__badge" title={`${it.badgeTitle} ${n}건`}>
                            {n > 99 ? "99+" : n}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          <div className="sstoa-side__foot">
            <button type="button" className="sstoa-side__btn" onClick={logout} title="로그아웃">
              <NavSvg name="logout" size={17} /><span>로그아웃</span>
            </button>
          </div>
        </aside>
        <main className="sstoa-main">{children}</main>
      </div>
    </div>
  );
}

export default function SstoaApp() {
  return (
    <Routes>
      <Route path="login" element={<Login />} />
      <Route
        index
        element={
          <Gate>
            <Shell><SstoaHome /></Shell>
          </Gate>
        }
      />
      <Route
        path="companies"
        element={
          <Gate>
            <Shell><CompanyList /></Shell>
          </Gate>
        }
      />
      <Route
        path="companies/new"
        element={
          <Gate>
            <Shell><CompanyForm /></Shell>
          </Gate>
        }
      />
      <Route
        path="companies/:id"
        element={
          <Gate>
            <Shell><CompanyDetail /></Shell>
          </Gate>
        }
      />
      <Route
        path="companies/:id/edit"
        element={
          <Gate>
            <Shell><CompanyForm /></Shell>
          </Gate>
        }
      />
      <Route
        path="projects"
        element={
          <Gate>
            <Shell><ProjectList /></Shell>
          </Gate>
        }
      />
      <Route
        path="projects/new"
        element={
          <Gate>
            <Shell><ProjectForm /></Shell>
          </Gate>
        }
      />
      <Route
        path="projects/:id"
        element={
          <Gate>
            <Shell><ProjectDetail /></Shell>
          </Gate>
        }
      />
      <Route
        path="projects/:id/edit"
        element={
          <Gate>
            <Shell><ProjectForm /></Shell>
          </Gate>
        }
      />
      <Route
        path="email"
        element={
          <Gate>
            <Shell><EmailCompose /></Shell>
          </Gate>
        }
      />
      <Route
        path="schedule"
        element={
          <Gate>
            <Shell><SchedulePage /></Shell>
          </Gate>
        }
      />
      <Route
        path="calendar"
        element={
          <Gate>
            <Shell><CalendarPage /></Shell>
          </Gate>
        }
      />
      <Route
        path="issues"
        element={
          <Gate>
            <Shell><IssuesPage /></Shell>
          </Gate>
        }
      />
      <Route
        path="parts"
        element={
          <Gate>
            <Shell><PartsPage /></Shell>
          </Gate>
        }
      />
      {/* 프로젝트 제안은 개발 문의로 합쳤다 (2026-11) */}
      <Route path="proposals" element={<Navigate to="/sstoa/inquiries" replace />} />
      <Route
        path="leads"
        element={
          <Gate>
            <Shell><LeadsPage /></Shell>
          </Gate>
        }
      />
      <Route
        path="cards"
        element={
          <Gate>
            <Shell><CardsAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="landing"
        element={
          <Gate>
            <Shell><LandingAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="inquiries"
        element={
          <Gate>
            <Shell><InquiriesPage /></Shell>
          </Gate>
        }
      />
      <Route
        path="site-works"
        element={
          <Gate>
            <Shell><WorksAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="site-products"
        element={
          <Gate>
            <Shell><ProductsAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="laona-deposits"
        element={
          <Gate>
            <Shell><LaonaDepositsAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="shop-orders"
        element={
          <Gate>
            <Shell><ShopOrdersAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="site-members"
        element={
          <Gate>
            <Shell><MembersAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="forum"
        element={
          <Gate>
            <Shell><ForumAdmin /></Shell>
          </Gate>
        }
      />
      <Route
        path="resources"
        element={
          <Gate>
            <Shell><ResourcesAdmin /></Shell>
          </Gate>
        }
      />
      {/* 서류 관리(목록) + 작성 (견적/계약/인보이스) */}
      {[
        ["quotes", "QUOTE"],
        ["orders", "PO"],
        ["contracts", "CONTRACT"],
        ["invoices", "INVOICE"],
      ].flatMap(([path, dtype]) => [
        <Route
          key={path}
          path={path}
          element={
            <Gate>
              <Shell><DocumentTypeList docType={dtype} /></Shell>
            </Gate>
          }
        />,
        <Route
          key={`${path}-new`}
          path={`${path}/new`}
          element={
            <Gate>
              <Shell><DocumentCompose docType={dtype} /></Shell>
            </Gate>
          }
        />,
      ])}
      <Route
        path="documents"
        element={
          <Gate>
            <Shell><DocumentsList /></Shell>
          </Gate>
        }
      />
      {/* 기본 진입: 토큰 있으면 홈, 없으면 로그인 */}
      <Route
        path="*"
        element={<Navigate to={getToken() ? "/sstoa" : "/sstoa/login"} replace />}
      />
    </Routes>
  );
}
